import React, { useCallback, useMemo, useRef } from 'react'
import { fieldNameByLang, getElementFieldValueWithLanguage, makeSetHandlerWithLanguage, makeValidationHandlerWithLanguage, useLanguageSwitch } from '../../utils/Language'
import { hasErrorsAll, makeSetHandlerPlain, makeValidationHandler } from '../../globalStates/utils'
import { ReferenceDataType, withReferenceDataState } from '../../globalStates/ReferenceDataHOC'
import { Product, useExhibitorDataState } from '../../globalStates/ExhibitorDataState'
import { useConfigDataState } from '../../globalStates/ConfigDataState'
import ProductInformationContents from './ProductInformationContents'
import { hasServiceKeys } from '../../globalStates/ConfigDataState'
import { useLanguageState } from '../../globalStates/LanguageState'
import CategoriesContents from '../categories/CategoriesContents'
import MultimediaContents from '../multimedia/MutimediaContents'
import { Downgraded, State, useState } from '@hookstate/core'
import DateTimeField from '../../components/Field/DateTime'
import TextAreaField from '../../components/Field/TextArea'
import PageContents from '../../components/PageContents'
import KeywordContents from '../profile/KeywordContents'
import FilePicker from '../../components/FilePicker'
import TextField from '../../components/Field/Text'
import { Tabs, Tab } from '../../components/Tabs'
import Section from '../../components/Section'
import LanguageSwitch from '../languageSwitch'
import { useDrag, useDrop } from 'react-dnd'
import { Col, Row } from 'react-bootstrap'


interface ProductSectionProps {
    productState: State<Product>
    index: number
    onOrderChanged: (productId: string, index: number) => void
    onClickRemove: (productId: string) => void
}

const ProductSection: React.FC<ProductSectionProps & ReferenceDataType> = ({ productState, refDataState, index, onOrderChanged, onClickRemove }) => {
    const branding = useLanguageState().getStrings()
    const exiState = useExhibitorDataState()
    const product = useState(productState)
    const isMonoLingual = branding.configuration.monoLingual ? 'De' : 'En'
    const { currentLanguage, onChangeLanguageHandler } = useLanguageSwitch(isMonoLingual);
    const sectionRef = useRef<HTMLDivElement>(null)
    const serviceKeys = useConfigDataState().getServiceKeys()
    const refCategories = useMemo(() => refDataState.getCategories("PRODUCT", branding.configuration.productsPage.sctProduct.categoryWhitelist), [branding.configuration.productsPage.sctProduct.categoryWhitelist, refDataState])
    const categoryVisible = serviceKeys.category ? serviceKeys.category > 0 && refCategories.length > 0 : false
    const keywordsVisible = hasServiceKeys(serviceKeys.keywords)

    const onChangeLogo = useCallback((image: File, onUploadProgress, onSuccessCallback, onErrorCallback) => {
        exiState.upload("LOGO", image, onUploadProgress)
            .then(response => {
                onSuccessCallback();
                if (response) {
                    product.logoFileId.set(response.fileId)
                    product.logoUrl.set(response.fileUrl)
                }
            })
            .catch(reason => onErrorCallback(reason))
    }, [exiState, product])

    const onRemoveLogo = useCallback(() => {
        product.logoFileId.set('')
        product.logoUrl.set('')
    }, [product])

    const onClickRemoveHandler = useCallback(() => {
        onClickRemove(product.id.get())
    }, [onClickRemove, product])

    const [{ isDragging }, drag] = useDrag({
        type: "Product",
        item: { productId: product.id.get() },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (item, monitor) => {
            // const { id: droppedId, originalIndex } = item
            const didDrop = monitor.didDrop()
            if (!didDrop) {
                console.log('!didDrop', item)
            }
        }
    })
    drag(sectionRef)

    const [, drop] = useDrop({
        accept: "Product",
        hover(droppedItem: any) {
            if (droppedItem.productId !== product.id.get()) {
                onOrderChanged(droppedItem.productId, index)
            }
        },
        drop(droppedItem: any) {
            onOrderChanged(droppedItem.productId, index)
        }
    })
    drop(sectionRef)

    return (
        <Section
            ref={sectionRef}
            title={product.name.get() || product.nameDe.get() || branding.configuration.productsPage.sctProduct.titleEmptyProduct}
            collapsible
            opacity={isDragging ? 0 : 1}
            onClickRemove={onClickRemoveHandler}
            entityState={product}>
            <Row>
                <Col xs={3}>
                    <FilePicker
                        name="companyLogo"
                        label={branding.configuration.productsPage.sctProduct.lblProductImage}
                        message={branding.configuration.productsPage.sctProduct.lblProductImageMessage}
                        browseLabel={branding.configuration.productsPage.sctProduct.btnBrowseFiles}
                        deleteLabel={branding.configuration.productsPage.sctProduct.btnDeleteImg}
                        placeholderTitle={branding.configuration.productsPage.sctProduct.hntTitle}
                        placeholderText={branding.configuration.productsPage.sctProduct.hntProp}
                        value={product.logoUrl.get()}
                        onChangeImage={onChangeLogo}
                        onRemoveImage={onRemoveLogo}
                    />
                </Col>
                <Col xs={9}>
                    <Row>
                        <Col>
                            <LanguageSwitch
                                id={`product_${index}_language_switch`}
                                name={`product_${index}_languages`}
                                value={currentLanguage}
                                onChange={onChangeLanguageHandler}
                                entityState={productState}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextField
                                id={fieldNameByLang("name", currentLanguage)}
                                name={fieldNameByLang("name", currentLanguage)}
                                label={branding.configuration.productsPage.sctProduct.lblProductname}
                                placeholder={branding.configuration.productsPage.sctProduct.hntProductname}
                                validate={makeValidationHandlerWithLanguage(product, 'name', currentLanguage)}
                                value={getElementFieldValueWithLanguage(product, "name", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(product, 'name', currentLanguage)}
                                required
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <DateTimeField
                                id="showFrom"
                                name="showFrom"
                                label={branding.configuration.productsPage.sctProduct.lblProductPublicationStart}
                                placeholder={branding.configuration.productsPage.sctProduct.hntProductPublicationStart}
                                validate={makeValidationHandler(product, 'showFrom')}
                                value={product.attach(Downgraded).get().showFrom}
                                onChange={(datetime: Date) => {
                                    product.showFrom.set(datetime)
                                }}
                            />
                        </Col>
                        <Col>
                            <DateTimeField
                                id="showUntil"
                                name="showUntil"
                                label={branding.configuration.productsPage.sctProduct.lblProductPublicationEnd}
                                placeholder={branding.configuration.productsPage.sctProduct.hntProductPublicationEnd}
                                validate={makeValidationHandler(product, 'showUntil')}
                                value={product.attach(Downgraded).get().showUntil}
                                onChange={(datetime: Date) => {
                                    product.showUntil.set(datetime)
                                }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextAreaField
                                name={fieldNameByLang("description", currentLanguage)}
                                label={branding.configuration.productsPage.sctProduct.lblDescriptionDetail}
                                placeholder={branding.configuration.productsPage.sctProduct.hntDescriptionDetail}
                                value={getElementFieldValueWithLanguage(product, "description", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(product, 'description', currentLanguage)}
                                limit={4000}
                                style={{ height: '100px' }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextAreaField
                                name={fieldNameByLang("teaser", currentLanguage)}
                                label={branding.configuration.productsPage.sctProduct.lblDescriptionShort}
                                placeholder={branding.configuration.productsPage.sctProduct.hntDescriptionShort}
                                value={getElementFieldValueWithLanguage(product, "teaser", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(product, 'teaser', currentLanguage)}
                                limit={200}
                                style={{ height: '80px' }}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
            {/*TODO get rid of these */}
            <br />
            <br />
            <Row>
                <Col xs={12}>
                    <Tabs>
                        <Tab eventKey="productInformation" title={branding.configuration.productsPage.sctProduct.lblTabProductInformation}>
                            <PageContents description={branding.configuration.productsPage.sctProduct.lblProductInformationDescription}>
                                <ProductInformationContents index={index} product={product} />
                            </PageContents>
                        </Tab>
                        {categoryVisible &&
                            <Tab eventKey="categories" title={branding.configuration.productsPage.sctProduct.lblTabCategories}>
                                <PageContents description={branding.configuration.productsPage.sctProduct.lblCategoriesDescription}>
                                    <CategoriesContents selectedCategoriesStates={product.categories} refCategories={refCategories} limit={serviceKeys.category} countableCategories={branding.configuration.countableCategories.productCountableCategories} />
                                </PageContents>
                            </Tab>
                        }
                        <Tab eventKey="multimedia" title={branding.configuration.productsPage.sctProduct.lblTabMultimedia} tabClassName={hasErrorsAll(product.medias) ? "invalid" : ""}>
                            <PageContents description={branding.configuration.productsPage.sctProduct.lblMultimediaDescription}>
                                <MultimediaContents mediaStates={product.medias} index={index} multimediaLimit={branding.configuration.productsPage.sctProduct.multimediaLimit} />
                            </PageContents>
                        </Tab>
                        {keywordsVisible &&
                            <Tab eventKey="keywords" title={branding.configuration.productsPage.sctProduct.lblTabKeywords}>
                                <PageContents description="">
                                    <KeywordContents value={product.tags.get()} onChange={makeSetHandlerPlain(product, 'tags')} />
                                </PageContents>
                            </Tab>
                        }
                    </Tabs>
                </Col>
            </Row>
        </Section>
    );
}

export default React.memo(withReferenceDataState(ProductSection))
